import React, { useMemo, useState } from 'react';
import { Alert, Box, Button, FormControl } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { type GridRenderCellParams, type GridValueFormatterParams } from '@mui/x-data-grid';
import { Link } from 'react-router-dom';
import AutocompleteDropdown from 'components/common/autocompleteDropdown/AutocompleteDropdown';
import { BusinessPicker } from 'components/common/businessPicker/BusinessPicker';
import {
  CentsDataGridServer,
  DEFAULT_PAGE_SIZE_OPTIONS,
} from 'components/common/dataGrid/dataGridServer';
import SearchInput from 'components/common/search/SearchInput';
import { DeviceType } from 'api/admin/washboard/washboard';
import { BulkDeviceConfiguration } from './BulkDeviceConfiguration';
import { useWashboardBulkActions } from './useWashboardBulkActions';
import { DEVICE_TYPE_OPTIONS, useWashboardFilters } from './useWashboardFilters';

const formatOptional = ({ value }: GridValueFormatterParams<string | null>) => value || 'N/A';

const COLUMNS = [
  {
    headerName: 'MAC',
    field: 'name',
    flex: 1,
    renderCell: (params: GridRenderCellParams) => (
      <Link to={`/washboard/${params.row.id}`} onClick={(e) => e.stopPropagation()}>
        {params.value}
      </Link>
    ),
  },
  {
    headerName: 'Device Type',
    field: 'deviceType',
    flex: 0.7,
    valueFormatter: formatOptional,
  },
  {
    headerName: 'Device Status',
    field: 'status',
    flex: 0.7,
    valueFormatter: formatOptional,
  },
  {
    headerName: 'Business',
    field: 'businessName',
    flex: 1,
    valueFormatter: formatOptional,
  },
  {
    headerName: 'Location',
    field: 'storeName',
    flex: 1,
    valueFormatter: formatOptional,
  },
  {
    headerName: 'Machine Name',
    field: 'machineName',
    flex: 0.9,
    sortable: false,
    valueFormatter: formatOptional,
  },
  {
    headerName: 'Machine Type',
    field: 'machineType',
    flex: 0.9,
    valueFormatter: formatOptional,
  },
  {
    headerName: 'Communication',
    field: 'communicationProtocol',
    flex: 1.1,
    sortable: false,
    valueFormatter: formatOptional,
  },
  {
    headerName: 'FW version',
    field: 'releaseVersion',
    flex: 0.8,
    sortable: false,
    valueFormatter: formatOptional,
  },
  {
    headerName: 'System Status',
    field: 'systemStatus',
    flex: 0.9,
    valueFormatter: formatOptional,
  },
  {
    headerName: 'IP Address',
    field: 'ipAddress',
    flex: 0.9,
    valueFormatter: formatOptional,
  },
];

const WashboardTable: React.FC = () => {
  const [selectedRowIds, setSelectedRowIds] = useState<Array<number | string>>([]);
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  const {
    pageNumber,
    pageSize,
    searchTerm,
    setSearchTerm,
    selectedBusinessId,
    setSelectedBusinessId,
    setSelectedStoreId,
    setSelectedDeviceType,
    sortModel,
    storeOptions,
    selectedStoreOption,
    selectedDeviceTypeOption,
    devices,
    total,
    isLoading,
    storesError,
    devicesError,
    refetch,
    setPageSize,
    handlePageChange,
    handleSortModelChange,
  } = useWashboardFilters();
  const errorMessage = storesError || devicesError;

  const selectedDevices = useMemo(
    () => devices.filter((device) => selectedRowIds.includes(device.id)),
    [devices, selectedRowIds],
  );

  const {
    reinitializeMutation,
    rebootMutation,
    downloadLogsMutation,
    handleReinitialize,
    handleReboot,
    handleDownloadLogs,
  } = useWashboardBulkActions({
    selectedDevices,
    onComplete: () => setSelectedRowIds([]),
  });

  const handleOpenPanel = () => setIsPanelOpen(true);
  const handleClosePanel = () => setIsPanelOpen(false);

  const handleSaveSuccess = () => {
    refetch();
    setSelectedRowIds([]);
  };

  return (
    <div className="page-container">
      <div className="washboard-header">
        <h1>Washboard</h1>
        <Button
          variant="contained"
          color="primary"
          className="washboard-edit-config-button"
          disabled={selectedRowIds.length === 0}
          onClick={handleOpenPanel}
        >
          Edit Configuration
        </Button>
      </div>
      {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

      <Grid container spacing={2} alignItems="center" className="washboard-filters-row">
        <Grid size={{ xs: 12, md: 4 }}>
          <SearchInput
            label="General Search by Any Column"
            value={searchTerm}
            onSearch={(value: string) => setSearchTerm(value)}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 2 }}>
          <FormControl fullWidth>
            <BusinessPicker
              id="washboard-business"
              selectedBusinessId={selectedBusinessId}
              onChange={setSelectedBusinessId}
              label="Filter by Business"
              size="small"
            />
          </FormControl>
        </Grid>
        <Grid size={{ xs: 6, md: 2 }}>
          <FormControl fullWidth>
            <AutocompleteDropdown
              id="washboard-store"
              key={selectedBusinessId ?? 'no-business'}
              selectedData={selectedStoreOption}
              onChange={(option) => {
                const value = option?.value;
                setSelectedStoreId(typeof value === 'number' ? value : null);
              }}
              label="Filter by Location"
              selectData={storeOptions}
              size="small"
              disabled={!selectedBusinessId}
            />
          </FormControl>
        </Grid>
        <Grid size={{ xs: 6, md: 2 }}>
          <FormControl fullWidth>
            <AutocompleteDropdown
              id="washboard-device-type"
              selectedData={selectedDeviceTypeOption}
              onChange={(option) => {
                const value = option?.value as DeviceType;
                const selectedDeviceType =
                  value && Object.values(DeviceType).includes(value) ? value : null;
                setSelectedDeviceType(selectedDeviceType);
              }}
              label="Filter by Device Type"
              selectData={DEVICE_TYPE_OPTIONS}
              size="small"
            />
          </FormControl>
        </Grid>
      </Grid>

      <Box className="washboard-actions-row">
        <Box className="washboard-actions">
          <Button
            variant="contained"
            color="primary"
            disabled={selectedRowIds.length === 0 || reinitializeMutation.isLoading}
            onClick={handleReinitialize}
          >
            Reinitialize
          </Button>
          <Button
            variant="outlined"
            color="primary"
            disabled={selectedRowIds.length === 0 || rebootMutation.isLoading}
            onClick={handleReboot}
          >
            Reboot
          </Button>
          <Button
            variant="outlined"
            color="primary"
            disabled={selectedRowIds.length === 0 || downloadLogsMutation.isLoading}
            onClick={handleDownloadLogs}
          >
            Download Logs
          </Button>
        </Box>
        <Box className="washboard-summary">
          <span>{`Selected Devices: ${selectedRowIds.length}`}</span>
          <span className="washboard-summary-divider">|</span>
          <span>{`Displaying ${total} devices`}</span>
        </Box>
      </Box>

      <CentsDataGridServer
        tableName="WASHBOARD"
        data={devices}
        columns={COLUMNS}
        isLoading={isLoading}
        rowCount={total}
        pageSize={pageSize}
        setPageSize={setPageSize}
        pageSizeOptions={DEFAULT_PAGE_SIZE_OPTIONS}
        page={pageNumber}
        onPageChange={handlePageChange}
        checkboxSelection
        rowSelectionModel={selectedRowIds}
        onRowSelectionModelChange={(newSelection) => {
          setSelectedRowIds(newSelection);
        }}
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
      />

      <BulkDeviceConfiguration
        isOpen={isPanelOpen}
        onClose={handleClosePanel}
        selectedDevices={selectedDevices}
        onSaveSuccess={handleSaveSuccess}
      />
    </div>
  );
};

export default WashboardTable;
